import { useRef } from "react";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import jantscherImg from "@/assets/captain-jantscher.webp";
import sarkariaImg from "@/assets/captain-sarkaria.webp";
import korkmazImg from "@/assets/captain-korkmaz.webp";
import placeholderImg from "@/assets/captain-placeholder.webp";

const IMAGES = { jantscher: jantscherImg, sarkaria: sarkariaImg, korkmaz: korkmazImg };

const cardIn = (i) => ({
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, delay: (i % 4) * 0.08, ease: [0.22, 1, 0.36, 1] } },
});

export const AiTag = ({ className = "" }) => (
  <span data-testid="ai-modified-tag" className={`pointer-events-none select-none font-mono-accent text-[7px] uppercase tracking-[0.2em] text-white/30 sm:text-[8px] ${className}`}>
    AI Modified
  </span>
);

const AiSide = () => (
  <AiTag className="absolute right-[13px] top-1/2 origin-right -translate-y-1/2 rotate-90 whitespace-nowrap" />
);

const num = (i) => String(i + 1).padStart(2, "0");

const TiltShell = ({ children, testId, index, className = "" }) => {
  const ref = useRef(null);
  const frame = useRef(0);
  const onMove = (e) => {
    const { clientX, clientY } = e;
    if (frame.current) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = 0;
      tilt(clientX, clientY);
    });
  };
  const tilt = (cx, cy) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (cx - r.left) / r.width;
    const y = (cy - r.top) / r.height;
    el.style.setProperty("--ry", `${(x - 0.5) * 14}deg`);
    el.style.setProperty("--rx", `${(0.5 - y) * 14}deg`);
    el.style.setProperty("--mx", `${x * 100}%`);
    el.style.setProperty("--my", `${y * 100}%`);
    el.style.setProperty("--gx", `${x * 100}%`);
    el.style.setProperty("--gy", `${y * 100}%`);
  };
  const onLeave = () => {
    cancelAnimationFrame(frame.current);
    frame.current = 0;
    ["--rx", "--ry"].forEach((k) => ref.current.style.setProperty(k, "0deg"));
  };
  return (
    <motion.div variants={cardIn(index)} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
      <article
        ref={ref}
        data-testid={testId}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        className={`gl-card-tilt group relative aspect-[3/4.3] w-full overflow-hidden rounded-[22px] ${className}`}
      >
        {children}
      </article>
    </motion.div>
  );
};

const CardFrame = ({ bright }) => (
  <div
    className={`pointer-events-none absolute inset-[7px] rounded-[16px] border ${
      bright ? "border-white/25" : "border-white/[0.07]"
    }`}
  />
);

export const RevealedCard = ({ captain, index }) => (
  <TiltShell
    testId={`captain-card-${index}`}
    index={index}
    className="border border-gl-blue/50 bg-[#0a1230] shadow-[0_0_0_1px_rgba(58,123,255,0.15),0_24px_60px_rgba(0,0,0,0.55),0_0_40px_rgba(58,123,255,0.18)]"
  >
    <span className="pointer-events-none absolute -right-2 top-6 select-none font-display text-[7.5rem] font-extrabold leading-none text-white/[0.07] sm:text-[9rem]">
      {num(index)}
    </span>
    <img
      src={IMAGES[captain.image]}
      alt={`Captain ${captain.name}`}
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover object-[50%_15%] transition-transform duration-700 ease-out group-hover:scale-[1.06]"
    />
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#050a1f] via-[#050a1f]/70 via-30% to-transparent to-60%" />
    <div className="gl-card-glare pointer-events-none absolute inset-0" />
    <CardFrame bright />
    <AiSide />

    <div className="absolute left-4 top-4 flex flex-col items-center sm:left-5 sm:top-5">
      <span className="font-display text-2xl font-extrabold leading-none text-white drop-shadow sm:text-3xl" data-testid={`captain-number-${index}`}>
        {num(index)}
      </span>
      <span className="mt-1 font-mono-accent text-[9px] uppercase tracking-[0.28em] text-gl-blue2">CPT</span>
    </div>
    <span className="absolute right-4 top-4 flex items-center gap-1.5 rounded-full border border-gl-blue/50 bg-[#0a1230]/80 px-1.5 py-1.5 sm:px-2.5 sm:py-1 font-mono-accent text-[9px] uppercase tracking-[0.2em] text-white sm:right-5 sm:top-5">
      <span className="h-1.5 w-1.5 rounded-full bg-gl-blue shadow-[0_0_8px_rgba(58,123,255,1)]" />
      <span className="hidden sm:inline">Enthüllt</span>
    </span>

    <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
      <div className="mb-3 h-px w-full bg-gradient-to-r from-transparent via-gl-blue/70 to-transparent" />
      <h3 data-testid={`captain-name-${index}`} className="text-center font-display text-lg font-extrabold uppercase leading-[1.05] tracking-tight text-white sm:text-xl">
        {captain.name}
      </h3>
      <div className="mt-3 flex flex-wrap justify-center gap-1.5">
        {captain.tags.map((t) => (
          <span key={t} className="rounded-full border border-white/15 bg-white/[0.08] px-2 py-0.5 font-mono-accent text-[9px] uppercase tracking-[0.14em] text-white/85">
            {t}
          </span>
        ))}
      </div>
    </div>
  </TiltShell>
);

export const LockedCard = ({ index }) => (
  <TiltShell
    testId={`captain-locked-card-${index}`}
    index={index}
    className="border border-white/10 bg-[#070b1c] shadow-[0_24px_60px_rgba(0,0,0,0.5)]"
  >
    <img
      src={placeholderImg}
      alt="Unbekannter Captain"
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover object-top opacity-40"
    />
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#04071a] via-[#04071a]/60 to-[#04071a]/30" />
    <div className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(0deg,rgba(255,255,255,0.025)_0px,rgba(255,255,255,0.025)_1px,transparent_1px,transparent_4px)]" />
    <div className="animate-gl-scan pointer-events-none absolute inset-x-0 top-0 h-1/4 bg-gradient-to-b from-transparent via-gl-blue/15 to-transparent">
      <div className="absolute inset-x-0 top-1/2 h-px bg-gl-blue/50 shadow-[0_0_12px_rgba(58,123,255,0.8)]" />
    </div>
    <div className="gl-card-glare pointer-events-none absolute inset-0" />
    <CardFrame />
    <AiSide />

    <span className="pointer-events-none absolute -right-2 top-6 select-none font-display text-[7.5rem] font-extrabold leading-none text-white/[0.04] sm:text-[9rem]">
      {num(index)}
    </span>
    <div className="absolute left-4 top-4 flex flex-col items-center sm:left-5 sm:top-5">
      <span className="font-display text-2xl font-extrabold leading-none text-white/50 sm:text-3xl">{num(index)}</span>
      <span className="mt-1 font-mono-accent text-[9px] uppercase tracking-[0.28em] text-white/35">CPT</span>
    </div>
    <span className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full border border-white/12 bg-black/50 text-white/60 sm:right-5 sm:top-5">
      <Lock className="h-3.5 w-3.5" />
    </span>

    <div className="absolute inset-x-0 bottom-0 p-4 text-center sm:p-5">
      <div className="mb-3 h-px w-full bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      <span className="block font-display text-2xl font-extrabold tracking-[0.2em] text-white/70 sm:text-3xl">???</span>
      <span className="mt-2 block font-mono-accent text-[9px] uppercase tracking-[0.26em] text-white/45">Bald enthüllt</span>
    </div>
  </TiltShell>
);
